import { CommunicationWindowSchema, OUTBOUND_CALL_AND_SMS_ALLOWED_COUNTRIES, isDefined, } from 'notable-common';
import { z } from 'zod';
import { FlowDurationUnits } from '../../../duration/duration.js';
import { FlowDurationSchemaWithMax } from '../../../duration/withMax.js';
import { FlowProducedType } from '../../../flowRun/producedType.js';
import { FlowBlockValueUsPhoneNumberSchema } from '../../blockValue/phoneNumberSchema.js';
import { BaseFlowBlockSchema, FlowBlockReturnTypeSchema } from '../block.js';
import { FlowBlockType } from '../type.js';
import { VoiceCallModel } from './model.js';
import { VoiceCallTransferCallToolDestinationSchema } from './transferCallTool.js';
import { END_CALL_MESSAGE_MAX_LENGTH, VOICEMAIL_MESSAGE_MAX_LENGTH, VoiceCallMessageSchema, VoiceCallTool, } from './vapi.js';
import { CartesiaVoicePersona, ElevenLabsVoicePersona, OPENAI_REALTIME_COMPATIBLE_VOICES, OpenAiRealtimeVoicePersona, OpenAiStandardVoicePersona, VoiceCallBackgroundSound, VoiceCallFirstMessageMode, VoiceCallIdleMessage, VoiceCallLanguage, VoiceCallOptionMode, VoiceProvider, isElevenLabsCompatibleVoicePersona, isRealTimeExclusiveVoicePersona, isRealTimeModel, isRealtimeCompatibleVoicePersona, } from './voice.js';
const FLOW_VOICE_SPEED_RANGE_BY_VOICE_PROVIDER = {
    [VoiceProvider.Cartesia]: {
        min: 0.6,
        max: 1.5,
        displayName: 'Cartesia',
    },
    [VoiceProvider.ElevenLabs]: {
        min: 0.7,
        max: 1.2,
        displayName: 'ElevenLabs',
    },
};
export const BaseFlowVoiceCallBlockConfigSchema = z.object({
    calleePhoneNumber: FlowBlockValueUsPhoneNumberSchema({
        isRequired: true,
        allowedCountries: OUTBOUND_CALL_AND_SMS_ALLOWED_COUNTRIES,
    }),
    callerPhoneNumberId: z.uuid().optional(),
    firstMessage: z.string(),
    firstMessageMode: z
        .enum(VoiceCallFirstMessageMode)
        .optional()
        .default(VoiceCallFirstMessageMode.AssistantWaitsForUser),
    prompt: z.string(),
    modelName: z.enum(VoiceCallModel).optional().default(VoiceCallModel.gpt_4_1),
    voicemailMessage: z.string().max(VOICEMAIL_MESSAGE_MAX_LENGTH).optional(),
    language: z.enum(VoiceCallLanguage).optional().default(VoiceCallLanguage.English),
    voiceProvider: z.enum(VoiceProvider).optional().default(VoiceProvider.ElevenLabs),
    voicePersona: z
        .union([
        z.enum(ElevenLabsVoicePersona),
        z.enum(OpenAiStandardVoicePersona),
        z.enum(OpenAiRealtimeVoicePersona),
        z.enum(CartesiaVoicePersona),
    ])
        .optional()
        .default(ElevenLabsVoicePersona.Sarah),
    timeout: FlowDurationSchemaWithMax({
        pathName: 'timeout',
        max: { value: 4, units: FlowDurationUnits.Hours },
    }),
    maxCallDuration: FlowDurationSchemaWithMax({
        pathName: 'maxCallDuration',
        max: { value: 1, units: FlowDurationUnits.Hours },
    }).optional(),
    tools: z.array(z.enum(VoiceCallTool)).optional(),
    decisionTreeId: z.string().nullish(),
    metadata: z.string().optional(),
    endCallMessage: z.string().max(END_CALL_MESSAGE_MAX_LENGTH).optional(),
    emotionRecognition: z.enum(VoiceCallOptionMode).optional(),
    endCallFunction: z.enum(VoiceCallOptionMode).optional(),
    startSpeakingSmartEndpointing: z.enum(VoiceCallOptionMode).optional(),
    backgroundDenoising: z.enum(VoiceCallOptionMode).optional(),
    backgroundSound: z
        .enum(VoiceCallBackgroundSound)
        .optional()
        .default(VoiceCallBackgroundSound.Off),
    startSpeakingWaitSeconds: z.number().min(0).max(5).optional().default(2.0),
    voiceSimilarityBoost: z.number().min(0).max(1).optional().default(0.6),
    voiceSpeed: z.number().optional().default(1.0),
    voiceStability: z.number().min(0).max(1).optional().default(0.3),
    idleMessages: z.array(z.enum(VoiceCallIdleMessage)).optional(),
    idleMessageMaxSpokenCount: z.number().min(1).max(10).optional().default(3),
    idleTimeoutSeconds: z.number().min(5).max(60).optional().default(5),
    stopSpeakingNumberOfWords: z.number().min(0).max(10).optional().default(5),
    stopSpeakingVoiceSeconds: z.number().min(0).max(0.5).optional().default(0.5),
    stopSpeakingVoiceBackOffSeconds: z.number().min(0).max(10).optional().default(0.5),
    modelTemperature: z.number().min(0).max(2).optional().default(0.3),
    transferCallTool: z
        .object({
        destinations: z.array(VoiceCallTransferCallToolDestinationSchema),
    })
        .optional(),
    toolToToolDescriptionMap: z
        .partialRecord(z.union([z.enum(VoiceCallTool), z.literal('TransferCall'), z.literal('AssistantHandoff')]), z.string())
        .optional(),
    enableSsmlParsing: z.boolean().optional().default(false),
    communicationWindow: CommunicationWindowSchema.optional(),
    validHours: z
        .object({
        start: z.number().min(0).max(23),
        end: z.number().min(0).max(23),
    })
        .optional(),
    preAssistantMessage: z.string().optional(),
});
export const FlowVoiceCallBlockConfigSchema = BaseFlowVoiceCallBlockConfigSchema.refine((config) => {
    // If using a realtime model, must use OpenAI voice provider
    if (isDefined(config.modelName) && isRealTimeModel(config.modelName)) {
        return config.voiceProvider === VoiceProvider.OpenAi;
    }
    return true;
}, {
    message: 'OpenAI Realtime LLM Models require OpenAI as the voice provider. Please select OpenAI voice provider.',
    path: ['voiceProvider'],
})
    .refine((config) => {
    // If using 11Labs voice provider, must use 11Labs voice persona
    if (config.voiceProvider === VoiceProvider.ElevenLabs && isDefined(config.voicePersona)) {
        return isElevenLabsCompatibleVoicePersona(config.voicePersona);
    }
    return true;
}, {
    message: 'Voice Provider is set to 11Labs, but the selected Voice is not an 11Labs voice. Please select an 11Labs voice or change the provider.',
    path: ['voicePersona'],
})
    .refine((config) => {
    // If using a realtime model, the voice must be realtime-compatible
    if (isDefined(config.modelName) &&
        isRealTimeModel(config.modelName) &&
        isDefined(config.voicePersona)) {
        return isRealtimeCompatibleVoicePersona(config.voicePersona);
    }
    return true;
}, {
    message: `LLM Model is a realtime model, but the selected Voice is not realtime-compatible. Valid voices: ${OPENAI_REALTIME_COMPATIBLE_VOICES.join(', ')}.`,
    path: ['voicePersona'],
})
    .refine((config) => {
    // If using realtime-exclusive voices, must use a realtime model
    if (isDefined(config.voicePersona) && isRealTimeExclusiveVoicePersona(config.voicePersona)) {
        return isDefined(config.modelName) && isRealTimeModel(config.modelName);
    }
    return true;
}, {
    message: `Voice is a realtime-exclusive voice (${Object.values(OpenAiRealtimeVoicePersona).join('/')}), but the LLM Model is not a realtime model. Please select a realtime model or choose a different voice.`,
    path: ['voicePersona'],
})
    .superRefine((config, ctx) => {
    if (isDefined(config.voiceProvider) && isDefined(config.voiceSpeed)) {
        const voiceSpeedRange = config.voiceProvider in FLOW_VOICE_SPEED_RANGE_BY_VOICE_PROVIDER
            ? FLOW_VOICE_SPEED_RANGE_BY_VOICE_PROVIDER[config.voiceProvider]
            : undefined;
        if (isDefined(voiceSpeedRange)) {
            if (config.voiceSpeed < voiceSpeedRange.min || config.voiceSpeed > voiceSpeedRange.max) {
                ctx.addIssue({
                    path: ['voiceSpeed'],
                    code: 'custom',
                    message: `Voice Provider is set to ${voiceSpeedRange.displayName}, but the voice speed is not between ${String(voiceSpeedRange.min)} and ${String(voiceSpeedRange.max)}. Please set the voice speed between ${String(voiceSpeedRange.min)} and ${String(voiceSpeedRange.max)}.`,
                });
            }
        }
    }
});
export const FlowVoiceCallBlockSchema = BaseFlowBlockSchema.extend({
    type: z.literal(FlowBlockType.VoiceCall),
    config: FlowVoiceCallBlockConfigSchema,
    returnType: FlowBlockReturnTypeSchema(FlowProducedType.VoiceCall),
});
export const FlowVoiceCallReturnDataSchema = z.looseObject({
    // data returned from executeVoiceCallBlock
    callId: z.string().optional(),
    phoneNumberId: z.string().optional(),
    type: z.string(),
    // data added from createVoiceCall
    notableVoiceCall: z
        .looseObject({
        id: z.string(),
        created: z.string(),
        updated: z.string(),
        sessionId: z.string(),
        providerCallId: z.string(),
        type: z.string(),
        assistant: z.record(z.string(), z.unknown()),
        toPhoneNumber: z.string(),
        fromPhoneNumber: z.string(),
        callStartTime: z.string(), // date-time string
        provider: z.string(),
        callEndTime: z.string().nullable().optional(), // date-time string
        endedReason: z.string().nullable().optional(),
        callCost: z.record(z.string(), z.looseObject({}).or(z.undefined())).nullable().optional(),
        summary: z.string().nullable().optional(),
        successEvaluation: z.string().nullable().optional(),
        gsPath: z.string().nullable().optional(),
    })
        .optional(),
    // data added in handleVoiceCallBlockCallback
    analysis: z
        .object({
        summary: z.string().optional(),
        successEvaluation: z.string().optional(),
    })
        .optional(),
    summary: z.string().optional(),
    transcript: z.string().optional(),
    messages: z.array(VoiceCallMessageSchema).optional(),
    durationSeconds: z.number().optional(),
    endedReason: z.string().optional(),
});
//# sourceMappingURL=config.js.map